/**
 * Whitelist of allowed redirect paths for auth callbacks.
 * These paths are extracted from the application routes in DefaultContent.tsx
 * to prevent open redirect vulnerabilities.
 */
const ALLOWED_REDIRECT_PATHS = [
  '/',
  '/curve-list',
  '/market-data-report',
  '/pricing',
  '/asset-master',
  '/portfolio-setup',
  '/portfolios',
  '/indexes',
  '/valuation-runs',
  '/valuation-range-runs',
  '/classification-runs',
  '/risk-runs',
  '/risk-details',
  '/split-flags',
  '/entities',
  '/entity-master',
] as const;

/**
 * Validates that a redirect URL is safe and allowed.
 * Only allows relative paths that match the application's route whitelist.
 *
 * @param redirectUrl - The URL to validate
 * @returns True if the URL is safe to redirect to, false otherwise
 */
export function isAllowedRedirectUrl(redirectUrl: string | undefined): boolean {
  if (!redirectUrl) {
    return false;
  }

  try {
    // Prevent absolute URLs (including protocol-relative URLs like //evil.com)
    if (redirectUrl.includes('://') || redirectUrl.startsWith('//')) {
      return false;
    }

    // Ensure it starts with a forward slash (relative path)
    if (!redirectUrl.startsWith('/')) {
      return false;
    }

    // Extract the base path (everything before query params or hash)
    const urlWithoutQueryOrHash = redirectUrl.split('?')[0].split('#')[0];

    // Check if the path matches any allowed base path
    return ALLOWED_REDIRECT_PATHS.some((allowedPath) => {
      // Exact match
      if (urlWithoutQueryOrHash === allowedPath) {
        return true;
      }
      // Match with dynamic segments (e.g., /curve-list/someCurve)
      if (allowedPath !== '/' && urlWithoutQueryOrHash.startsWith(`${allowedPath}/`)) {
        return true;
      }
      return false;
    });
  } catch {
    // If any error occurs during parsing, reject the URL
    return false;
  }
}

/**
 * Sanitizes a redirect URL by validating it against the whitelist.
 * Returns the sanitized URL if valid, or a safe fallback URL if invalid.
 *
 * @param redirectUrl - The URL to sanitize
 * @param fallbackUrl - The fallback URL to use if validation fails (defaults to '/')
 * @returns A safe redirect URL
 */
export function sanitizeRedirectUrl(redirectUrl: string | undefined, fallbackUrl: string = '/'): string {
  return isAllowedRedirectUrl(redirectUrl) ? redirectUrl! : fallbackUrl;
}
